/**
 * PreviewView — §预览. The real-engine 逐拍预览 surface, restyled with the cream
 * design system.
 *
 * This is a presentational, prop-driven restyle of the original `PreviewPane`.
 * It renders the **shared production renderer** `PlayerStage` (so the visual is
 * pixel-identical to the desktop IDE preview + the shipped player) driven by the
 * slot `StageState` + `PlayerDecision` the container's `PreviewController` walks
 * out of `/compile`'s `Episode.steps`. The bound asset urls live ON those steps
 * (the compiler resolves symbol → asset), so images render with no extra fetch
 * here — the container only hands us the already-resolved `stage`.
 *
 * The real-engine step logic is PRESERVED verbatim from the original PreviewPane:
 *  - `useTypewriter` reveals dialogue / narrator / you lines char-by-char.
 *  - Tap order mirrors ls-preview `onTap`: a tap mid-reveal COMPLETES the line
 *    (does not advance); a settled-line tap advances one beat (`onNext`).
 *  - Video CGs gate the 继续 button on `<video>` onEnded; image CGs continue
 *    immediately. A fresh decision re-arms the gate.
 *  - choice / cg_show decisions are surfaced by PlayerStage; minigame halts are
 *    auto-skipped upstream in the controller.
 *
 * This view OWNS no data and never fetches. The container wires `onCompile` /
 * `onNext` / `onChoose` / `onCgContinue` to its `PreviewController`. See the
 * prop docs below for the optional `source` / `episodeId` / `onCompiled`
 * convenience props the integrator may pass through.
 */

import { useCallback, useEffect, useState, type ReactElement } from "react";
import { Clapperboard, Play, RotateCcw, TriangleAlert } from "lucide-react";
import {
  PlayerStage,
  useTypewriter,
  type ChoiceOptionView,
  type PlayerDecision,
  type StageState,
} from "@lunaverse-ide/shared/stage-player";
import "@lunaverse-ide/shared/stage-player/player.generated.css";
import { Button, EmptyState, Surface } from "../../ui/index.js";
import { useLocale, useTr, type Entry } from "../../i18n/context.js";
import "./PreviewView.css";

/** View-local bilingual copy for the preview surface. */
const S = {
  title: { zh: "读一读，玩一玩", en: "Read it. Play it." },
  idleDesc: {
    zh: "你的对白、选择和故事，会在这里成为玩家的体验。",
    en: "Your dialogue, choices and story come to life here.",
  },
  staleTitle: { zh: "预览已过期", en: "Preview is out of date" },
  staleSourceDesc: {
    zh: "剧本内容已更新。重新编译后，预览会使用最新文本。",
    en: "The script changed. Recompile to preview the latest text.",
  },
  staleAssetsDesc: {
    zh: "素材绑定已更新。重新编译后，预览会使用最新素材。",
    en: "Asset bindings changed. Recompile to preview the latest media.",
  },
  unavailableTitle: { zh: "暂时无法预览", en: "Preview unavailable" },
  unavailableDesc: {
    zh: "当前服务未配置 Lunascript 编译器，暂时不能生成真实预览。",
    en: "This server has no Lunascript compiler configured, so real preview is unavailable.",
  },
  compileAndPreview: { zh: "开始预览", en: "Start preview" },
  compiling: { zh: "准备中…", en: "Preparing…" },
  diagTitle: { zh: "编译未通过", en: "Compile failed" },
  recompile: { zh: "更新预览", en: "Update preview" },
  episodeEnd: { zh: "本集结束", en: "Episode complete" },
  restart: { zh: "重新预览", en: "Replay" },
} satisfies Record<string, Entry>;

/** One compiled, asset-resolved step the engine walks. Opaque to the view —
 *  only the container's controller reads its shape. Re-exported for the
 *  `onCompiled` callback signature so consumers don't reach into `lunaria-server`. */
export type PreviewStep = Record<string, unknown>;

export interface PreviewViewProps {
  /**
   * The raw `.ls` source of the active episode. Optional / informational: this
   * view does NOT compile it itself (no direct fetch) — the container compiles
   * via its own client and feeds the resulting `stage`/`decision`. Pass it
   * through if you want the view to reflect which source is loaded; it is not
   * required for rendering.
   */
  readonly source?: string;
  /** The active episode id, for display / keying. Optional. */
  readonly episodeId?: string;
  /**
   * Fired by the container after a successful compile, carrying the resolved
   * steps. The view never calls this itself — it's a passthrough hook the
   * container may invoke when it builds its `PreviewController`. Declared here so
   * the prop interface documents the full compile contract for the integrator.
   */
  readonly onCompiled?: (steps: readonly PreviewStep[]) => void;

  /** Current slot stage from the controller. null before any compile (idle). */
  readonly stage?: StageState | null;
  /** Active choice / cg decision from the controller (null when none). */
  readonly decision?: PlayerDecision | null;
  /** Compile diagnostics — when non-empty, the error state is shown. */
  readonly diagnostics?: readonly string[];
  /** True while a compile is in flight (busy state). */
  readonly compiling?: boolean;
  /** Whether 编译并预览 is actionable (an episode is selected). */
  readonly canCompile?: boolean;
  /** Whether the server exposes a compiler at all. */
  readonly compilerAvailable?: boolean;
  /** Why the current compiled preview was invalidated, when known. */
  readonly staleReason?: "source" | "assets" | null;
  /** Run the real compiler and (re)build the preview. */
  readonly onCompile?: () => void;
  /** Advance one beat (tap on a settled line). */
  readonly onNext?: () => void;
  /** Pick a choice option (descends into its sub-steps). */
  readonly onChoose?: (optionId: string) => void;
  /** Resume past a cg_show overlay. */
  readonly onCgContinue?: () => void;
  /** True once the walk reached the episode end. Surfaces the restart affordance. */
  readonly isDone?: boolean;
  /** Restart the preview from the top (re-compile). */
  readonly onRestart?: () => void;
}

export function PreviewView(props: PreviewViewProps): ReactElement {
  const { stage, diagnostics, compiling, canCompile } = props;
  const tr = useTr();

  // ── error state: compile diagnostics ──
  if (diagnostics && diagnostics.length > 0) {
    return (
      <div className="lc-preview">
        <Surface
          elevation="raised"
          radius="4"
          pad="md"
          className="lc-preview__diag"
          role="alert"
        >
          <div className="lc-preview__diag-head">
            <TriangleAlert size={16} aria-hidden="true" />
            <span>{tr(S.diagTitle)}</span>
          </div>
          <ul className="lc-preview__diag-list">
            {diagnostics.map((d, i) => (
              <li key={i} className="lc-preview__diag-line">
                {d}
              </li>
            ))}
          </ul>
          <div className="lc-preview__diag-action">
            <Button
              variant="primary"
              size="sm"
              loading={compiling}
              leadingIcon={<RotateCcw size={15} />}
              onClick={props.onCompile}
            >
              {compiling ? tr(S.compiling) : tr(S.recompile)}
            </Button>
          </div>
        </Surface>
      </div>
    );
  }

  // ── idle state: nothing compiled yet ──
  if (!stage) {
    const compilerAvailable = props.compilerAvailable ?? true;
    const title = !compilerAvailable
      ? tr(S.unavailableTitle)
      : props.staleReason
        ? tr(S.staleTitle)
        : tr(S.title);
    const description = !compilerAvailable
      ? tr(S.unavailableDesc)
      : props.staleReason === "source"
        ? tr(S.staleSourceDesc)
        : props.staleReason === "assets"
          ? tr(S.staleAssetsDesc)
          : tr(S.idleDesc);
    return (
      <div className="lc-preview lc-preview--idle">
        <EmptyState
          icon={<Clapperboard size={22} />}
          title={title}
          description={description}
          action={
            <Button
              variant="primary"
              loading={compiling}
              disabled={!canCompile}
              leadingIcon={<Play size={16} />}
              onClick={props.onCompile}
            >
              {compiling ? tr(S.compiling) : tr(S.compileAndPreview)}
            </Button>
          }
        />
      </div>
    );
  }

  // ── live state ──
  return <LiveStage {...props} stage={stage} decision={props.decision ?? null} />;
}

/**
 * The live stage — split out so `useTypewriter` (a hook) only runs once a stage
 * actually exists (the idle / error branches return before any hooks). The
 * engine interaction here is a verbatim preservation of the original PreviewPane.
 */
function LiveStage(
  props: PreviewViewProps & { stage: StageState; decision: PlayerDecision | null },
): ReactElement {
  const { stage, decision, onNext, onChoose, onCgContinue, isDone, onRestart, compiling } = props;
  const tr = useTr();
  const { locale } = useLocale();

  // Active line text drives the typewriter; phone lines + decisions reveal
  // nothing (pass "" to reset the reveal). Mirrors ls-preview App.tsx.
  const lineText =
    stage.line &&
    (stage.line.kind === "dialogue" || stage.line.kind === "narrator" || stage.line.kind === "you")
      ? stage.line.text
      : "";
  const tw = useTypewriter(lineText);

  // Video CGs gate the "继续" button on the <video> reporting onEnded; image CGs
  // can continue immediately. A fresh decision (each cg_show) re-arms the gate;
  // the player flips it true on video end.
  const [cgVideoEnded, setCgVideoEnded] = useState(false);
  useEffect(() => {
    setCgVideoEnded(false);
  }, [decision]);

  // Tap order mirrors ls-preview `onTap`: a tap mid-reveal COMPLETES the line
  // (it does not advance); a settled-line tap advances one beat.
  const onTap = useCallback(() => {
    if (tw.isTyping) {
      tw.complete();
      return;
    }
    onNext?.();
  }, [tw, onNext]);

  const onSelectChoice = useCallback(
    (option: ChoiceOptionView) => {
      onChoose?.(option.id);
    },
    [onChoose],
  );

  return (
    <div className="lc-preview lc-preview--live">
      {props.staleReason && <div className="lc-preview__end" role="status">
        <span>{tr({ zh: "正文有新修改，当前试玩进度已保留", en: "New edits are ready. Your playthrough is preserved." })}</span>
        <Button size="sm" variant="secondary" loading={compiling} onClick={props.onCompile}>{tr(S.recompile)}</Button>
      </div>}
      <div className="lc-preview__frame">
        <PlayerStage
          stage={stage}
          protagonistName={null}
          language={locale === "en" ? "en" : "zh-CN"}
          revealCount={tw.revealCount}
          isTyping={tw.isTyping}
          decision={decision}
          cgVideoEnded={cgVideoEnded}
          blocked={isDone}
          onTap={onTap}
          onSelectChoice={onSelectChoice}
          onCgContinue={onCgContinue}
          onCgVideoEnded={() => setCgVideoEnded(true)}
        />
      </div>
      {isDone ? (
        <div className="lc-preview__end" role="status">
          <span className="lc-preview__end-label">{tr(S.episodeEnd)}</span>
          <Button
            variant="secondary"
            size="sm"
            loading={compiling}
            leadingIcon={<RotateCcw size={15} />}
            onClick={onRestart}
          >
            {tr(S.restart)}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default PreviewView;
